import {
  Circle,
  Focus,
  Hand,
  Highlighter,
  Pause,
  Pencil,
  Play,
  RotateCcw,
  SkipForward,
  Sparkles,
  Underline as UnderlineIcon,
  X,
  ZoomIn,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ScaledStage } from '@/components/canvas/ScaledStage';
import { createAnnotation, createRevealStep } from '@/lib/factories';
import type {
  Annotation,
  AnnotationKind,
  DrawStroke,
  RevealStep,
  RevealStepAudio,
} from '@/lib/schema.types';
import { cn, uid } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import { AiTeachDialog } from './AiTeachDialog';
import { AudioEditor } from './AudioEditor';
import { Stage } from './Stage';
import { StepInspector } from './StepInspector';
import { StepTimeline } from './StepTimeline';

export type TeachTool = 'cursor' | AnnotationKind | 'pen' | 'highlighter';

interface Props {
  slideId: string;
  onClose: () => void;
}

export function TeachStudio({ slideId, onClose }: Props) {
  const slide = useEditorStore((s) => s.script?.slides.find((x) => x.id === slideId) ?? null);
  const actions = useEditorActions();
  const audioRef = useRef<HTMLAudioElement>(null);

  const steps = useMemo(() => slide?.revealSteps ?? [], [slide?.revealSteps]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(steps[0]?.id ?? null);
  const [tool, setTool] = useState<TeachTool>('cursor');
  const [aiOpen, setAiOpen] = useState(false);

  // Playback state — manual step-through. `playIndex` is the currently shown
  // step. `playing === true` means we're in playback mode (stage hides
  // unrevealed elements). Each click of the toolbar Next button increments
  // `playIndex`; user explicitly clicks Stop to exit.
  const [playing, setPlaying] = useState(false);
  const [playIndex, setPlayIndex] = useState(0);

  // Keep the selected step in sync if it gets removed.
  useEffect(() => {
    if (selectedStepId && !steps.some((s) => s.id === selectedStepId)) {
      setSelectedStepId(steps[0]?.id ?? null);
    } else if (!selectedStepId && steps.length > 0) {
      setSelectedStepId(steps[0].id);
    }
  }, [steps, selectedStepId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Seed the timeline so every element on the slide appears as a reveal step.
  // Runs once per slide on mount: any element not already referenced by an
  // existing reveal step gets appended as its own "Reveal element" step in
  // document order. Elements the user later removes from the timeline stay
  // removed for the rest of the session.
  useEffect(() => {
    const state = useEditorStore.getState();
    const liveSlide = state.script?.slides.find((s) => s.id === slideId);
    if (!liveSlide) return;

    const referenced = new Set<string>();
    (liveSlide.revealSteps ?? []).forEach((s) =>
      (s.elementIds ?? []).forEach((id) => referenced.add(id)),
    );

    const missing: string[] = [];
    liveSlide.rows.forEach((row) =>
      row.columns.forEach((col) =>
        col.elements.forEach((el) => {
          if (!referenced.has(el.id)) missing.push(el.id);
        }),
      ),
    );

    if (missing.length === 0) return;

    let firstNewId: string | null = null;
    missing.forEach((elementId) => {
      const step: RevealStep = {
        ...createRevealStep('Reveal element'),
        elementIds: [elementId],
      };
      if (!firstNewId) firstNewId = step.id;
      state.upsertRevealStep(slideId, step);
    });

    if (!selectedStepId && firstNewId) {
      setSelectedStepId(firstNewId);
    }
    // We intentionally only run this on mount / when the active slide changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideId]);

  const selectedStep: RevealStep | null =
    steps.find((s) => s.id === selectedStepId) ?? null;

  // Effective view state — depends on whether we're playing.
  const visibleElementIds: Set<string> | null = useMemo(() => {
    if (!playing) return null; // edit mode: show everything
    const set = new Set<string>();
    for (let i = 0; i <= playIndex && i < steps.length; i++) {
      (steps[i].elementIds ?? []).forEach((id) => set.add(id));
    }
    return set;
  }, [playing, playIndex, steps]);

  const visibleAnnotations: Annotation[] = useMemo(() => {
    if (!playing) return selectedStep?.annotations ?? [];
    const out: Annotation[] = [];
    for (let i = 0; i <= playIndex && i < steps.length; i++) {
      (steps[i].annotations ?? []).forEach((a) => out.push(a));
    }
    return out;
  }, [playing, playIndex, steps, selectedStep]);

  // Mirror annotations: edit mode shows the selected step's strokes; playback
  // accumulates strokes from every revealed step.
  const visibleDrawStrokes: DrawStroke[] = useMemo(() => {
    if (!playing) return selectedStep?.drawStrokes ?? [];
    const out: DrawStroke[] = [];
    for (let i = 0; i <= playIndex && i < steps.length; i++) {
      (steps[i].drawStrokes ?? []).forEach((s) => out.push(s));
    }
    return out;
  }, [playing, playIndex, steps, selectedStep]);

  // A finished pen/highlighter stroke → append to the selected step (or a new
  // "Drawing" step when none is selected).
  const onStrokeComplete = (stroke: Omit<DrawStroke, 'id'>) => {
    if (playing) return;
    const full: DrawStroke = { ...stroke, id: uid('stroke') };
    if (selectedStep) {
      actions.upsertRevealStep(slideId, {
        ...selectedStep,
        drawStrokes: [...(selectedStep.drawStrokes ?? []), full],
      });
    } else {
      const step: RevealStep = { ...createRevealStep('Drawing'), drawStrokes: [full] };
      actions.upsertRevealStep(slideId, step, steps.length);
      setSelectedStepId(step.id);
    }
  };

  // ---------- playback driver (manual) ----------
  const stopPlayback = useCallback(() => {
    setPlaying(false);
    setPlayIndex(0);
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
  }, []);

  // When the playing step changes, sync timeline selection + (re)play its audio.
  useEffect(() => {
    if (!playing) return;
    if (playIndex >= steps.length) return;

    const step = steps[playIndex];
    setSelectedStepId(step.id);

    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (step.audio?.src) {
      audioEl.src = step.audio.src;
      audioEl.playbackRate = step.audio.playbackSpeed ?? 1;
      audioEl.currentTime = 0;
      const playPromise = audioEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay rejected — silent; user can advance manually anyway.
        });
      }
    } else {
      audioEl.pause();
    }
  }, [playing, playIndex, steps]);

  if (!slide) return null;

  // ---------- step / element actions ----------
  const onAddEmptyStep = () => {
    const step = createRevealStep(`Step ${steps.length + 1}`);
    actions.upsertRevealStep(slideId, step);
    setSelectedStepId(step.id);
  };

  const onAddRevealElement = (elementId: string) => {
    const step: RevealStep = {
      ...createRevealStep(`Reveal element`),
      elementIds: [elementId],
    };
    actions.upsertRevealStep(slideId, step);
    setSelectedStepId(step.id);
  };

  // ---------- playback controls ----------
  const startPlayback = () => {
    if (steps.length === 0) return;
    setTool('cursor');
    setPlayIndex(0);
    setPlaying(true);
  };

  const advancePlayback = () => {
    setPlayIndex((i) => {
      const next = i + 1;
      if (next >= steps.length) {
        // Past the end — keep showing the final step but stop audio.
        const a = audioRef.current;
        if (a) a.pause();
      }
      return next;
    });
  };

  const restartPlayback = () => {
    setPlayIndex(0);
  };

  /**
   * Click an element on the Stage with an annotation tool selected.
   * Each annotation becomes its OWN step appended after the currently selected
   * one, so the timeline order strictly controls when an annotation appears
   * (and the user can reorder it just like any other step).
   */
  const onPickElement = (elementId: string) => {
    if (playing || tool === 'cursor') return;
    // Pen / highlighter draw freely on the Stage — they don't pick elements.
    if (tool === 'pen' || tool === 'highlighter') return;

    const annotation = createAnnotation(tool, elementId);

    // Zoom / spotlight must play UNDER the narration rather than after it. Attach
    // it to the selected step (which carries the audio) instead of spawning its
    // own silent step, so the offline player has speak() running while the
    // zoom/dim is active and the two overlap. Mirrors how pen strokes attach (see
    // onStrokeComplete). Falls through to a new step only when nothing is
    // selected (a slide with no narration step to sync against).
    if ((tool === 'zoom' || tool === 'spotlight') && selectedStep) {
      actions.upsertRevealStep(slideId, {
        ...selectedStep,
        elementIds: selectedStep.elementIds.includes(elementId)
          ? selectedStep.elementIds
          : [...selectedStep.elementIds, elementId],
        annotations: [...(selectedStep.annotations ?? []), annotation],
      });
      setTool('cursor');
      return;
    }

    const step: RevealStep = {
      ...createRevealStep(`${capitalize(tool)}`),
      // Include the target element so the annotation has something to render
      // against at this point in playback even if no earlier step revealed it.
      elementIds: [elementId],
      annotations: [annotation],
    };
    const insertAt = selectedStep
      ? steps.findIndex((s) => s.id === selectedStep.id) + 1
      : steps.length;
    actions.upsertRevealStep(slideId, step, insertAt);
    setSelectedStepId(step.id);
    setTool('cursor');
  };

  const onUpdateAudio = (audio: RevealStepAudio) => {
    if (!selectedStep) return;
    actions.upsertRevealStep(slideId, { ...selectedStep, audio });
  };

  const audio = selectedStep?.audio;
  const playingStep = playing ? steps[Math.min(playIndex, steps.length - 1)] ?? null : null;
  const atEnd = playing && playIndex >= steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg">
      <Header slideLabel={slide.label} onClose={onClose} onAiTeach={() => setAiOpen(true)} />

      <div className="flex flex-1 overflow-hidden">
        {/* Left rail — single timeline */}
        <aside className="flex w-[300px] flex-col border-r border-border-subtle bg-white">
          <StepTimeline
            slide={slide}
            steps={steps}
            selectedStepId={selectedStepId}
            playingStepId={playingStep?.id ?? null}
            onSelect={(id) => {
              if (playing) stopPlayback();
              setSelectedStepId(id);
            }}
            onAddRevealElement={onAddRevealElement}
            onAddEmpty={onAddEmptyStep}
            onDelete={(id) => actions.removeRevealStep(slideId, id)}
            onReorder={(ids) => actions.reorderRevealSteps(slideId, ids)}
          />
        </aside>

        {/* Center — stage */}
        <main className="relative flex flex-1 flex-col bg-[#f4f5fb]">
          <ToolbarStrip
            tool={tool}
            onChange={setTool}
            disabled={!selectedStep && tool !== 'cursor'}
            playing={playing}
            atEnd={atEnd}
            canPlay={steps.length > 0}
            onPlay={startPlayback}
            onNext={advancePlayback}
            onRestart={restartPlayback}
            onStop={stopPlayback}
            playLabel={
              playing ? `${Math.min(playIndex + 1, steps.length)} / ${steps.length}` : null
            }
          />

          {/* Landscape slide scaled to width; scrolls vertically when the
              slide is taller than the available area. */}
          <div className="flex-1 overflow-y-auto scroll-thin">
            <div className="mx-auto w-full max-w-[1100px] px-10 py-8 pb-24">
              <ScaledStage>
                <Stage
                  slide={slide}
                  annotations={visibleAnnotations}
                  drawStrokes={visibleDrawStrokes}
                  revealedElementIds={visibleElementIds}
                  tool={playing ? 'cursor' : tool}
                  onPickElement={onPickElement}
                  onStrokeComplete={onStrokeComplete}
                />
              </ScaledStage>
            </div>
          </div>

          {/* Always-mounted hidden audio so the play driver doesn't remount. */}
          <audio ref={audioRef} className="hidden" />

          {(audio?.src || playing) && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
              <div className="flex items-center gap-3 rounded-full bg-fg/90 px-4 py-2 text-white shadow-floating">
                {audio?.src && !playing && (
                  <audio
                    src={audio.src}
                    controls
                    className="h-8"
                    style={{ filter: 'invert(1)' }}
                  />
                )}
                {playing && (
                  <>
                    <button
                      onClick={stopPlayback}
                      className="grid h-8 w-8 place-items-center rounded-full bg-white/15 hover:bg-white/25"
                      title="Stop"
                    >
                      <Pause size={14} />
                    </button>
                    <span className="text-[12px] font-medium">
                      Step {Math.min(playIndex + 1, steps.length)} / {steps.length}
                    </span>
                    {atEnd ? (
                      <button
                        onClick={restartPlayback}
                        className="grid h-8 w-8 place-items-center rounded-full bg-white/15 hover:bg-white/25"
                        title="Restart playback"
                      >
                        <RotateCcw size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={advancePlayback}
                        className="grid h-8 w-8 place-items-center rounded-full bg-white/15 hover:bg-white/25"
                        title="Next step"
                      >
                        <SkipForward size={14} />
                      </button>
                    )}
                  </>
                )}
                {!playing && (
                  <span className="font-mono text-[11px] opacity-80">
                    {((audio?.durationMs ?? 0) / 1000).toFixed(1)}s
                  </span>
                )}
              </div>
            </div>
          )}
        </main>

        {/* Right rail — step inspector + audio editor */}
        <aside className="flex w-[360px] flex-col border-l border-border-subtle bg-white">
          <div className="max-h-[45%] overflow-y-auto border-b border-border-subtle px-3 py-2 scroll-thin">
            <StepInspector slideId={slideId} step={selectedStep} />
          </div>
          <div className="flex-1 overflow-hidden">
            <AudioEditor step={selectedStep} slide={slide} onUpdateAudio={onUpdateAudio} />
          </div>
        </aside>
      </div>

      <AiTeachDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        slide={slide}
        slideId={slideId}
      />
    </div>
  );
}

function Header({
  slideLabel,
  onClose,
  onAiTeach,
}: {
  slideLabel: string;
  onClose: () => void;
  onAiTeach: () => void;
}) {
  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-white px-5">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-fg">Teach Mode</span>
        <span className="text-fg-subtle">·</span>
        <span className="text-sm text-fg-muted">{slideLabel || 'Untitled slide'}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onAiTeach}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-[12px] font-medium text-fg hover:bg-bg-subtle"
        >
          <Sparkles size={13} /> AI Teach
        </button>
        <button
          onClick={onClose}
          className="grid h-8 w-8 place-items-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
          aria-label="Close teach mode"
        >
          <X size={15} />
        </button>
      </div>
    </header>
  );
}

const TOOLS: { id: TeachTool; label: string; icon: React.ReactNode }[] = [
  { id: 'cursor', label: 'Cursor', icon: <Hand size={13} /> },
  // 'annotate' tool removed — buggy element-pick + redundant with the other
  // annotation tools. The 'annotate' kind still renders for any existing data.
  { id: 'encircle', label: 'Encircle', icon: <Circle size={13} /> },
  { id: 'underline', label: 'Underline', icon: <UnderlineIcon size={13} /> },
  { id: 'highlight', label: 'Highlight', icon: <Highlighter size={13} /> },
  { id: 'zoom', label: 'Zoom', icon: <ZoomIn size={13} /> },
  { id: 'spotlight', label: 'Spotlight', icon: <Focus size={13} /> },
  { id: 'pen', label: 'Pen', icon: <Pencil size={13} /> },
  { id: 'highlighter', label: 'Marker', icon: <Highlighter size={13} /> },
];

function ToolbarStrip({
  tool,
  onChange,
  disabled,
  playing,
  atEnd,
  canPlay,
  onPlay,
  onNext,
  onRestart,
  onStop,
  playLabel,
}: {
  tool: TeachTool;
  onChange: (t: TeachTool) => void;
  disabled?: boolean;
  playing: boolean;
  atEnd: boolean;
  canPlay: boolean;
  onPlay: () => void;
  onNext: () => void;
  onRestart: () => void;
  onStop: () => void;
  playLabel: string | null;
}) {
  const toolsDisabled = disabled || playing;
  return (
    <div className="flex items-center justify-center pt-6">
      <div className="flex items-center gap-1 rounded-full border border-border bg-white px-2 py-1.5 shadow-pill">
        <button
          onClick={() => onChange('cursor')}
          className={cn(
            'grid h-7 w-7 place-items-center rounded-full text-fg-muted transition',
            tool === 'cursor' && !playing ? 'bg-bg-subtle text-fg' : 'hover:text-fg',
            playing && 'opacity-40',
          )}
          title="Cursor"
          disabled={playing}
        >
          <Hand size={13} />
        </button>
        <span className="h-5 w-px bg-border" />
        {TOOLS.filter((t) => t.id !== 'cursor').map((t) => {
          const active = tool === t.id && !playing;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              disabled={toolsDisabled}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition',
                active
                  ? 'bg-accent-soft text-accent'
                  : 'text-fg-muted hover:bg-bg-subtle hover:text-fg',
                toolsDisabled && 'opacity-40',
              )}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
        <span className="h-5 w-px bg-border" />
        {!playing ? (
          <button
            onClick={onPlay}
            disabled={!canPlay}
            className="inline-flex items-center gap-1.5 rounded-full bg-fg px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white transition hover:opacity-90 disabled:opacity-40"
            title="Play through reveal steps"
          >
            <Play size={12} /> Play
          </button>
        ) : (
          <>
            {atEnd ? (
              <button
                onClick={onRestart}
                className="inline-flex items-center gap-1.5 rounded-full bg-bg-subtle px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-fg-muted transition hover:text-fg"
                title="Restart playback"
              >
                <RotateCcw size={12} /> Restart
              </button>
            ) : (
              <button
                onClick={onNext}
                className="inline-flex items-center gap-1.5 rounded-full bg-bg-subtle px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-fg-muted transition hover:text-fg"
                title="Show next step"
              >
                <SkipForward size={12} /> Next {playLabel}
              </button>
            )}
            <button
              onClick={onStop}
              className="inline-flex items-center gap-1.5 rounded-full bg-fg px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white transition hover:opacity-90"
              title="Stop playback"
            >
              <Pause size={12} /> Stop
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
